import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import {
  createFuelDeliveryRequest,
  updateFuelDeliveryRequest,
} from 'features/fuelManagement/fuelManagementSlice'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { TitleText } from 'styling/Typography'
import {
  IFuelDeliveryRequestFormState,
  IFuelDeliveryRequestLineForm,
} from 'types/fuelDeliveryRequests.interface'
import { IFuelDeliveryRequestResponse } from 'types/api/fuelDeliveryRequestsApi.interface'
import { fuelTypeSelect, fuelTypes, parseFuelTankOptions } from '../fuelData'
import { parseCapacityUnits } from 'features/units/unitsMappers'
import { LineCard } from './FuelDeliveryRequests.styled'
import useFocusFirstInput from 'utils/useFocusFirstInput'

const emptyLine = (): IFuelDeliveryRequestLineForm => ({
  key: `${Date.now()}-${Math.random()}`,
  fuelType: null,
  quantity: null,
  unit: null,
  fuelTank: null,
})

const parseNamed = (data: any) =>
  (data?.data || data || []).map((item: any) => ({ label: item.name, value: item.id }))

const toFormState = (request?: IFuelDeliveryRequestResponse | null): IFuelDeliveryRequestFormState => {
  if (!request) {
    return {
      department: null,
      supplier: null,
      notes: '',
      lines: [emptyLine()],
    }
  }
  return {
    id: request.id,
    department: request.department
      ? { label: request.department.name, value: request.department.id }
      : null,
    supplier: request.supplier
      ? { label: request.supplier.name, value: request.supplier.id }
      : null,
    notes: request.notes || '',
    lines: (request.lines || []).map((line) => {
      const fuelOpt = fuelTypes.find((t) => t.value === line.fuelType)
      return {
        key: String(line.id),
        fuelType: fuelOpt
          ? { label: fuelOpt.label, value: line.fuelType }
          : { label: String(line.fuelType), value: line.fuelType },
        quantity: line.quantity,
        unit: line.unit ? { label: line.unit.name, value: line.unit.id } : null,
        fuelTank: line.fuelTank ? { label: line.fuelTank.name, value: line.fuelTank.id } : null,
      }
    }),
  }
}

export const FuelDeliveryRequestFormModal = ({
  closeModal,
  value,
}: {
  closeModal: () => void
  value?: IFuelDeliveryRequestResponse | null
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [state, setState] = useState<IFuelDeliveryRequestFormState>(toFormState(value))
  useFocusFirstInput()
  useModalClose(closeModal)

  const handleSelect = (v: ISelectValue, name: string) => {
    setState((prev) => ({ ...prev, [name]: v }))
  }

  const handleLineSelect = (index: number, name: string, v: ISelectValue) => {
    setState((prev) => {
      const lines = [...prev.lines]
      const next = { ...lines[index], [name]: v }
      if (name === 'fuelTank' && (v as any)?.fuelType != null) {
        next.fuelType = fuelTypeSelect((v as any).fuelType)
      }
      lines[index] = next
      return { ...prev, lines }
    })
  }

  const handleLineQuantity = (index: number, quantity: number) => {
    setState((prev) => {
      const lines = [...prev.lines]
      lines[index] = { ...lines[index], quantity }
      return { ...prev, lines }
    })
  }

  const addLine = () => setState((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }))

  const removeLine = (index: number) => {
    setState((prev) => ({
      ...prev,
      lines: prev.lines.length === 1 ? prev.lines : prev.lines.filter((_, i) => i !== index),
    }))
  }

  const canSave =
    Boolean(state.department?.value) &&
    state.lines.length > 0 &&
    state.lines.every(
      (line) =>
        line.fuelType?.value &&
        line.unit?.value &&
        line.fuelTank?.value &&
        line.quantity != null &&
        Number(line.quantity) > 0,
    )

  const handleSave = async () => {
    if (!canSave) return
    const body = {
      departmentId: Number(state.department.value),
      supplierId: state.supplier?.value ? Number(state.supplier.value) : undefined,
      notes: state.notes || undefined,
      lines: state.lines.map((line) => ({
        fuelType: Number(line.fuelType.value),
        quantity: Number(line.quantity),
        unitId: Number(line.unit.value),
        fuelTankId: Number(line.fuelTank.value),
      })),
    }
    let resp: any
    if (state.id) {
      resp = await dispatch(updateFuelDeliveryRequest({ id: state.id, body }))
    } else {
      resp = await dispatch(createFuelDeliveryRequest(body))
    }
    if (resp.meta.requestStatus === 'fulfilled') closeModal()
  }

  return (
    <Modal black width='720px'>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>
              {state.id ? 'Edit' : 'Create'} Fuel Delivery Request
            </TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <HonestSelectAsyncInput
            nomarg
            link='departments'
            name='department'
            labelText='Department'
            defaultValue={state.department?.label}
            onChange={(v) => handleSelect(v, 'department')}
            parserFunction={parseNamed}
            required
          />
          <HonestSelectAsyncInput
            nomarg
            link='fuel/management/supplier'
            name='supplier'
            labelText='Supplier (optional)'
            defaultValue={state.supplier?.label}
            onChange={(v) => handleSelect(v, 'supplier')}
            parserFunction={parseNamed}
          />
          <Input
            nomarg
            labelText='Notes'
            name='notes'
            placeholder='Optional notes…'
            value={state.notes}
            onChange={(e: any) => setState((prev) => ({ ...prev, notes: e.target.value }))}
          />
          {state.lines.map((line, index) => (
            <LineCard key={line.key}>
              <Flex between center>
                <TitleText weight='700'>Line {index + 1}</TitleText>
                {state.lines.length > 1 && (
                  <Button size='sm' variant='secondary' onClick={() => removeLine(index)}>
                    Remove
                  </Button>
                )}
              </Flex>
              <HonestSelectInput
                key={`line-fuel-type-${index}-${line.fuelType?.value || 'none'}`}
                nomarg
                name={`fuelType-${index}`}
                labelText='Fuel type'
                options={fuelTypes}
                defaultValue={line.fuelType?.value}
                onChange={(v: any) => handleLineSelect(index, 'fuelType', v)}
                disabled={Boolean(line.fuelTank?.value)}
              />
              <Input
                nomarg
                type='number'
                labelText='Quantity'
                name={`quantity-${index}`}
                value={line.quantity ?? ''}
                onChange={(e: any) =>
                  handleLineQuantity(index, e.target.value === '' ? null : Number(e.target.value))
                }
              />
              <HonestSelectAsyncInput
                nomarg
                link='units'
                name={`unit-${index}`}
                labelText='Unit'
                defaultValue={line.unit?.label}
                onChange={(v) => handleLineSelect(index, 'unit', v)}
                parserFunction={parseCapacityUnits}
              />
              <HonestSelectAsyncInput
                nomarg
                link='fuel/management/tank'
                name={`fuelTank-${index}`}
                labelText='Fuel tank'
                defaultValue={line.fuelTank?.label}
                onChange={(v) => handleLineSelect(index, 'fuelTank', v)}
                parserFunction={parseFuelTankOptions}
              />
            </LineCard>
          ))}
          <Button variant='secondary' onClick={addLine}>
            Add line
          </Button>
        </Flex>
        <Button
          style={{ marginTop: '1rem' }}
          disabled={!canSave}
          variant='primary'
          onClick={handleSave}
        >
          Save
        </Button>
      </Flex>
    </Modal>
  )
}
